import React from 'react'
import PropTypes from 'prop-types'
import {
    SimpleGrid,
    Text,
    Stack,
    useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {FormattedMessage, useIntl} from 'react-intl'
import ActionCard from '../../components/action-card/index'
import AddressDisplay from '../../components/address-display'
import {remCalc} from '../../theme/_utils'

const messages = {
    shippingSubtitle: {
        id: 'account_addresses.subtitle.shipping_address',
        defaultMessage: 'Shipping Address',
    },
    billingSubtitle: {
        id: 'account_addresses.subtitle.billing_address',
        defaultMessage: 'Billing Address',
    },
    editButton: {
        id: 'shipping_address.label.edit_button',
        defaultMessage: 'Edit {address}',
    },
    removeButton: {
        id: 'shipping_address.label.remove_button',
        defaultMessage: 'Remove {address}',
    },
    defaultBadge: {
        id: 'account_addresses.badge.default',
        defaultMessage: 'Default',
    },
}

const getAddressType = (address) => {
    if (address.c_isB2BAddress) return 'business'
    if (address.c_isB2CBillingAddress) return 'private'
    return 'shipping'
}

const AddressList = ({
    addresses,
    isEditing,
    selectedAddressId,
    toggleEdit,
    removeAddress,
    form,
    submitForm,
    editBtnRefs,
    hasAddresses,
    title,
    headingRef,
    ShippingAddressForm,
    onSetDefault,
    isBillingAddress,
    sectionRef,
}) => {
    const {formatMessage} = useIntl()
    const styles = useMultiStyleConfig('Account')
    const subtitleMessage =
        title === 'Shipping Address' ? messages.shippingSubtitle : messages.billingSubtitle

    return (
        <Stack gap={{base: remCalc(16), lg: remCalc(24)}} ref={sectionRef}>
            <Text
                as="h2"
                {...styles.shippingSubtitle}
                tabIndex="0"
                ref={headingRef}
                display={isEditing && 'none'}
            >
                <FormattedMessage {...subtitleMessage} />
            </Text>
            <SimpleGrid
                columns={isEditing ? 1 : [1, 1, 1, 2]}
                gridAutoFlow="row dense"
                gap={remCalc(24)}
                maxW={{lg: remCalc(900)}}
            >
                {addresses.map((address) => {
                    const editLabel = formatMessage(messages.editButton, {
                        address: address.address1,
                    })
                    const removeLabel = formatMessage(messages.removeButton, {
                        address: address.address1,
                    })
                    const addressType = getAddressType(address)

                    return (
                        <React.Fragment key={address.addressId}>
                            <ActionCard
                                variant="address-card"
                                key={address.addressId}
                                editBtnRef={editBtnRefs[address.addressId]}
                                onRemove={() => removeAddress(address.addressId)}
                                onEdit={() => toggleEdit(address)}
                                editBtnLabel={editLabel}
                                removeBtnLabel={removeLabel}
                                display={isEditing && 'none'}
                                isDefault={address.c_isPreferredAddress}
                                onSetDefault={() => onSetDefault(address.addressId)}
                                isAddressSection={true} // Pass true to indicate this is the addresses section
                            >
                                <AddressDisplay
                                    address={address}
                                    isConfirmation={true}
                                    isTitleBold={true}
                                    isAccount={true}
                                />
                            </ActionCard>

                            {isEditing && address.addressId === selectedAddressId && (
                                <ShippingAddressForm
                                    form={form}
                                    hasAddresses={hasAddresses}
                                    submitForm={submitForm}
                                    selectedAddressId={selectedAddressId}
                                    toggleEdit={toggleEdit}
                                    isBillingAddress={isBillingAddress}
                                    addressType={addressType}
                                />
                            )}
                        </React.Fragment>
                    )
                })}
            </SimpleGrid>
        </Stack>
    )
}

AddressList.propTypes = {
    addresses: PropTypes.array.isRequired,
    isEditing: PropTypes.bool.isRequired,
    selectedAddressId: PropTypes.string,
    toggleEdit: PropTypes.func.isRequired,
    removeAddress: PropTypes.func.isRequired,
    form: PropTypes.object.isRequired,
    submitForm: PropTypes.func.isRequired,
    editBtnRefs: PropTypes.object.isRequired,
    hasAddresses: PropTypes.bool.isRequired,
    title: PropTypes.string.isRequired,
    headingRef: PropTypes.object.isRequired,
    ShippingAddressForm: PropTypes.elementType.isRequired,
    onSetDefault: PropTypes.func.isRequired,
    isBillingAddress: PropTypes.bool,
    sectionRef: PropTypes.object,
}

export default AddressList
